import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authService } from '@/services/auth-service';
import { cryptoStore } from './crypto-store';
import { api } from '@/services/api';
import type { User } from '@/types/auth';
import { useSubscriptionStore } from './subscription-store';

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLocked: boolean;
  isLoading: boolean;

  login: (email: string, masterPassword: string, twoFactorCode?: string) => Promise<void>;
  register: (email: string, masterPassword: string) => Promise<void>;
  logout: () => Promise<void>;
  lock: () => void;
  unlock: (masterPassword: string) => Promise<void>;
  refreshTokens: () => Promise<void>;
  setTokens: (accessToken: string, refreshToken: string) => void;
  updateUser: (updates: Partial<User>) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => {
      // Set up API auth handlers
      api.setAuthHandlers(
        () => get().accessToken,
        async () => {
          try {
            await get().refreshTokens();
          } catch {
            get().logout();
          }
        }
      );

      return {
        user: null,
        accessToken: null,
        refreshToken: null,
        isAuthenticated: false,
        isLocked: false,
        isLoading: false,

        login: async (email, masterPassword, twoFactorCode) => {
          set({ isLoading: true });

          try {
            const prelogin = await authService.prelogin(email);

            const keys = await cryptoStore.getState().deriveKeys(
              masterPassword,
              prelogin.salt,
              {
                iterations: prelogin.kdfIterations,
                memory: prelogin.kdfMemory,
                parallelism: prelogin.kdfParallelism,
              }
            );

            const response = await authService.login({
              email,
              authKeyHash: keys.authKeyHash,
              twoFactorCode,
            });

            if (response.requiresTwoFactor) {
              throw new Error('2FA_REQUIRED');
            }

            cryptoStore.getState().setEncryptionKey(keys.encryptionKey);

            set({
              user: response.user,
              accessToken: response.accessToken,
              refreshToken: response.refreshToken,
              isAuthenticated: true,
              isLocked: false,
            });
          } finally {
            set({ isLoading: false });
          }
        },

        register: async (email, masterPassword) => {
          set({ isLoading: true });

          try {
            const crypto = cryptoStore.getState();
            const salt = crypto.generateSalt();

            const keys = await crypto.deriveKeys(masterPassword, salt);

            const defaultVaultNameEncrypted = await crypto.encrypt(
              'Meu Cofre',
              keys.encryptionKey
            );

            await authService.register({
              email,
              authKeyHash: keys.authKeyHash,
              salt,
              kdfIterations: 3,
              kdfMemory: 65536,
              kdfParallelism: 4,
              encryptionKeyVerifier: keys.verifier,
              defaultVaultNameEncrypted,
            });

            // Don't auto-login - user needs to verify email first
            // The register page will redirect to verify-email page
          } finally {
            set({ isLoading: false });
          }
        },

        logout: async () => {
          try {
            await authService.logout();
          } catch {
            // Ignore errors on logout
          } finally {
            cryptoStore.getState().clearEncryptionKey();
            useSubscriptionStore.getState().reset();
            set({
              user: null,
              accessToken: null,
              refreshToken: null,
              isAuthenticated: false,
              isLocked: false,
            });
          }
        },

        lock: () => {
          cryptoStore.getState().clearEncryptionKey();
          set({ isLocked: true });
        },

        unlock: async (masterPassword) => {
          const { user } = get();
          if (!user) throw new Error('User not found');

          set({ isLoading: true });

          try {
            const prelogin = await authService.prelogin(user.email);

            const keys = await cryptoStore.getState().deriveKeys(
              masterPassword,
              prelogin.salt,
              {
                iterations: prelogin.kdfIterations,
                memory: prelogin.kdfMemory,
                parallelism: prelogin.kdfParallelism,
              }
            );

            cryptoStore.getState().setEncryptionKey(keys.encryptionKey);

            set({ isLocked: false });
          } finally {
            set({ isLoading: false });
          }
        },

        refreshTokens: async () => {
          const { refreshToken } = get();
          if (!refreshToken) throw new Error('No refresh token');

          const response = await authService.refresh({ refreshToken });

          set({
            accessToken: response.accessToken,
            refreshToken: response.refreshToken,
          });
        },

        setTokens: (accessToken, refreshToken) => {
          set({ accessToken, refreshToken });
        },

        updateUser: (updates) => {
          const { user } = get();
          if (user) {
            set({ user: { ...user, ...updates } });
          }
        },
      };
    },
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        isLocked: state.isLocked,
      }),
    }
  )
);
